import FbButton from 'components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { cn } from 'components/UI/cn'
import { ChainSearchResult, DoorSearchResult } from 'features/elastic/searchers'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useUnifiedSearchCompanyUrl } from 'utils/getUnifiedSearchCompanyUrl'
import { CSVRow, InternalColumns } from '../importer-types'
import { useImporterViewModel, getColumnOption } from '../ImporterViewModel'
import { Tabs } from 'components/FbUI/Tabs'
import {
  setMatchedChainByHash,
  setMatchedDoorByHash,
} from '../importDataTransforms'

/** Fields to display in the "Your Input" column - company_match and primary_key types */
type MappedField = {
  mapped: string
  original: string
  label: string
}

export function ImportWizardReviewPendingStep({
  show,
  data,
}: {
  show: boolean
  data: CSVRow[]
}) {
  const viewModel = useImporterViewModel()
  const [cursor, setCursor] = useState(0)

  // Reset cursor when data changes (e.g., after deselecting in step 1)
  useEffect(() => {
    if (cursor >= data.length && data.length > 0) {
      setCursor(data.length - 1)
    } else if (data.length === 0) {
      setCursor(0)
    }
  }, [data.length, cursor])

  function handleNext() {
    if (cursor + 1 >= data.length) {
      return
    }
    setCursor(cursor + 1)
  }

  function handlePrev() {
    if (cursor - 1 < 0) {
      return
    }
    setCursor(cursor - 1)
  }

  // Handler to clear selection for current company
  const handleClearSelection = useCallback(() => {
    const hash = data[cursor][InternalColumns.COMPANY_HASH]
    viewModel.updateImportData((d) => {
      let result = setMatchedChainByHash(d, hash, null, false)
      result = setMatchedDoorByHash(result, hash, null, false)
      return result
    })
  }, [cursor, data, viewModel])

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      if (e.key === 'j') {
        e.preventDefault()
        if (cursor > 0) {
          setCursor(cursor - 1)
        }
      } else if (e.key === 'k') {
        e.preventDefault()
        if (cursor < data.length - 1) {
          setCursor(cursor + 1)
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [cursor, data.length])

  if (!show) {
    return null
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No pending records to review
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2 h-full">
      {/* Progress header */}
      <div className="flex items-center justify-between border-b pb-2">
        <div className="flex items-center gap-2">
          <div className="text-lg font-bold text-gray-700">
            {cursor + 1}
            <span className="text-gray-400"> / {data.length}</span>
          </div>
          <div className="h-1.5 w-32 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-fb-dark-green transition-all duration-300"
              style={{ width: `${((cursor + 1) / data.length) * 100}%` }}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <FbButton
              type="button"
              onClick={handleClearSelection}
              variant="secondary"
            >
              Clear
            </FbButton>
            <FbButton
              type="button"
              onClick={handlePrev}
              disabled={cursor === 0}
            >
              <Kbd>J</Kbd> ← Prev
            </FbButton>
            <FbButton
              type="button"
              onClick={handleNext}
              disabled={cursor === data.length - 1}
            >
              <Kbd>K</Kbd> Next →
            </FbButton>
          </div>
        </div>
      </div>

      {/* Review card */}
      <div className="flex-1 overflow-auto">
        <ReviewCard csvRow={data[cursor]} />
      </div>
    </div>
  )
}

function ReviewCard({ csvRow }: { csvRow: CSVRow }) {
  const viewModel = useImporterViewModel()
  type ReviewTab = 'chain' | 'door'
  const [tab, setTab] = useState<ReviewTab>('chain')

  // Read current selection directly from the row (single source of truth)
  const currentSelectedChain = csvRow[InternalColumns.MATCHED_CHAIN]
  const currentSelectedDoor = csvRow[InternalColumns.MATCHED_DOOR]
  const companyHash = csvRow[InternalColumns.COMPANY_HASH]

  // Sort and limit to top 5 suggestions by score
  const topChains = useMemo(() => {
    const sorted = [...(csvRow.__internal__suggested_chains ?? [])]
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
    // Add hidden placeholders to always have 5 items
    const placeholders = Array(5 - sorted.length).fill(null)
    return [...sorted, ...placeholders]
  }, [csvRow.__internal__suggested_chains])

  const topDoors = useMemo(() => {
    const sorted = [...(csvRow.__internal__suggested_doors ?? [])]
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
    // Add hidden placeholders to always have 5 items
    const placeholders = Array(5 - sorted.length).fill(null)
    return [...sorted, ...placeholders]
  }, [csvRow.__internal__suggested_doors])

  const onSelectChain = useCallback(
    (chain: ChainSearchResult) => {
      // If already selected, deselect it
      if (currentSelectedChain?.item_id === chain.item_id) {
        viewModel.updateImportData((d) =>
          setMatchedChainByHash(d, companyHash, null, false)
        )
        return
      }

      // Select this chain
      viewModel.updateImportData((d) =>
        setMatchedChainByHash(d, companyHash, chain, false)
      )
    },
    [companyHash, viewModel, currentSelectedChain]
  )

  const onSelectDoor = useCallback(
    (door: DoorSearchResult) => {
      // If already selected, deselect it
      if (currentSelectedDoor?.item_id === door.item_id) {
        viewModel.updateImportData((d) => {
          let result = setMatchedDoorByHash(d, companyHash, null, false)
          result = setMatchedChainByHash(result, companyHash, null, false)
          return result
        })
        return
      }

      // Select this door (and auto-select matching chain if available)
      const matchingChain =
        csvRow.__internal__suggested_chains?.find(
          (c) => c.item_id === door.chain_id
        ) ?? null

      viewModel.updateImportData((d) => {
        let result = setMatchedDoorByHash(d, companyHash, door, false)
        result = setMatchedChainByHash(
          result,
          companyHash,
          matchingChain,
          false
        )
        return result
      })
    },
    [
      companyHash,
      csvRow.__internal__suggested_chains,
      viewModel,
      currentSelectedDoor,
    ]
  )

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      // Tab switching
      if (e.key === 'u') {
        setTab('chain')
        e.preventDefault()
        return
      }
      if (e.key === 'i') {
        setTab('door')
        e.preventDefault()
        return
      }

      // Selection shortcuts (1-5)
      const num = parseInt(e.key)
      if (num >= 1 && num <= 5) {
        e.preventDefault()
        const index = num - 1

        if (tab === 'chain') {
          const chain = topChains[index]
          if (chain) {
            onSelectChain(chain)
          }
        } else {
          const door = topDoors[index]
          if (door) {
            onSelectDoor(door)
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [tab, topChains, topDoors, onSelectChain, onSelectDoor])

  // Get all mapped company fields (company_match and primary_key types)
  const mappedCompanyFields = useMemo((): MappedField[] => {
    return (
      viewModel.importData?.headers
        .filter((h) => {
          const option = getColumnOption(h.mapped)
          return (
            option?.type === 'company_match' || option?.type === 'primary_key'
          )
        })
        .map((h) => ({
          mapped: h.mapped,
          original: h.original,
          label: getColumnOption(h.mapped)?.name || h.mapped,
        })) || []
    )
  }, [viewModel.importData?.headers])

  // Build input fields dynamically from mapped headers
  const inputFields = useMemo(() => {
    return mappedCompanyFields.map((field) => ({
      label: field.label,
      value: String(csvRow[field.original] ?? '') || '-',
    }))
  }, [mappedCompanyFields, csvRow])

  // Handler for manual search
  const handleManualSearch = useCallback(() => {
    viewModel.setSelectedRowId(csvRow[InternalColumns.ROW_ID])
  }, [viewModel, csvRow])

  return (
    <div className="flex flex-col gap-2 h-full">
      {/* Comparison Summary */}
      <ComparisonSummary
        inputFields={inputFields}
        selectedChain={currentSelectedChain}
        selectedDoor={currentSelectedDoor}
        isChainManualMatch={csvRow[InternalColumns.IS_CHAIN_MANUAL_MATCH]}
        isDoorManualMatch={csvRow[InternalColumns.IS_DOOR_MANUAL_MATCH]}
      />

      {/* Suggestions Section */}
      <div className="flex-1 flex flex-col">
        <Tabs
          tabs={[
            {
              title: (
                <div className="flex flex-row items-center">
                  <Kbd>U</Kbd> &nbsp; Chains (
                  {Math.min(
                    csvRow.__internal__suggested_chains?.length || 0,
                    5
                  )}
                  )
                </div>
              ),
              key: 'chain',
            },
            {
              title: (
                <div className="flex flex-row items-center">
                  <Kbd>I</Kbd> &nbsp; Doors (
                  {Math.min(csvRow.__internal__suggested_doors?.length || 0, 5)}
                  )
                </div>
              ),
              key: 'door',
            },
          ]}
          selectedTab={tab}
          setSelectedTab={setTab}
        />

        <div className="mt-2 flex-1">
          {tab === 'chain' && (
            <div>
              {/* Header */}
              <div className="flex items-center gap-2 px-2 py-1 mb-1 text-sm font-medium text-gray-600 border-b">
                <div className="flex-shrink-0 w-6"></div>
                <div className="flex-shrink-0 w-4"></div>
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div>Name</div>
                  <div>Domain</div>
                </div>
              </div>
              {/* Cards */}
              <div className="space-y-1.5">
                {topChains.map((chain, index) =>
                  chain ? (
                    <SuggestionCard
                      key={chain.item_id}
                      data={chain}
                      isSelected={
                        chain.item_id === currentSelectedChain?.item_id
                      }
                      onSelect={() => onSelectChain(chain)}
                      number={index + 1}
                    />
                  ) : (
                    <div
                      key={`placeholder-chain-${index}`}
                      className="invisible"
                    >
                      <SuggestionCard
                        data={{
                          item_id: '',
                          name: '-',
                          domain: '-',
                          count: 0,
                          type: '',
                          score: 0,
                        }}
                        isSelected={false}
                        onSelect={() => {}}
                        number={index + 1}
                      />
                    </div>
                  )
                )}
                {/* Manual Search Button */}
                <ManualSearchCard onClick={handleManualSearch} />
              </div>
            </div>
          )}
          {tab === 'door' && (
            <div>
              {/* Header */}
              <div className="flex items-center gap-2 px-2 py-1 mb-1 text-sm font-medium text-gray-600 border-b">
                <div className="flex-shrink-0 w-6"></div>
                <div className="flex-shrink-0 w-4"></div>
                <div className="flex-1 grid grid-cols-3 gap-3">
                  <div>Name</div>
                  <div>Domain</div>
                  <div>Address</div>
                </div>
              </div>
              {/* Cards */}
              <div className="space-y-1.5">
                {topDoors.map((door, index) =>
                  door ? (
                    <SuggestionCard
                      key={door.item_id}
                      data={door}
                      isSelected={door.item_id === currentSelectedDoor?.item_id}
                      onSelect={() => onSelectDoor(door)}
                      number={index + 1}
                    />
                  ) : (
                    <div
                      key={`placeholder-door-${index}`}
                      className="invisible"
                    >
                      <SuggestionCard
                        data={{
                          item_id: '',
                          name: '-',
                          domain: '-',
                          latitude: 0,
                          longitude: 0,
                          address: '-',
                          city: '',
                          state: '',
                          country: '',
                          zipcode: '',
                          chain_id: '',
                          chain_name: '',
                          chain_size: 0,
                          type: '',
                          score: 0,
                        }}
                        isSelected={false}
                        onSelect={() => {}}
                        number={index + 1}
                      />
                    </div>
                  )
                )}
                {/* Manual Search Button */}
                <ManualSearchCard onClick={handleManualSearch} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ManualSearchCard({ onClick }: { onClick: () => void }) {
  return (
    <div
      className="border rounded px-2 py-1 cursor-pointer transition-all border-dashed border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
      onClick={onClick}
    >
      <div className="flex items-center justify-center gap-2 py-1">
        <div className="text-gray-600 font-medium">Manual Search</div>
      </div>
    </div>
  )
}

function SuggestionCard({
  data,
  isSelected,
  onSelect,
  number,
}: {
  data: ChainSearchResult | DoorSearchResult
  isSelected: boolean
  onSelect: () => void
  number?: number
}) {
  const { getCompanyUrlFromId } = useUnifiedSearchCompanyUrl()
  const hasAddress = 'address' in data && data.address
  const isDoor = 'chain_id' in data
  const chainId = isDoor ? data.chain_id : `${data.type}${data.item_id}`
  const doorId = isDoor ? `${data.type}${data.item_id}` : null

  // Build company URL for the name link
  const companyId = `${data.type}${data.item_id}`
  const companyUrl = getCompanyUrlFromId(companyId)

  return (
    <div
      className={`border rounded px-2 py-1 cursor-pointer transition-all ${
        isSelected
          ? 'border-green-500 bg-green-50'
          : 'border-gray-200 bg-white hover:border-gray-400'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2">
        {number && (
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-gray-200 text-gray-700 rounded font-semibold">
            {number}
          </div>
        )}
        <div
          className={cn('text-green-600 font-semibold whitespace-nowrap', {
            invisible: !isSelected,
          })}
        >
          ✓
        </div>
        <div className="flex-1">
          <div
            className={`grid ${hasAddress ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}
          >
            <div className="font-semibold text-gray-900">
              {companyUrl ? (
                <FbLink
                  to={companyUrl}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                >
                  {data.name || '-'}
                </FbLink>
              ) : (
                data.name || '-'
              )}
            </div>
            <div className="text-gray-900">{data.domain || '-'}</div>
            {hasAddress && (
              <div className="text-gray-900">{data.address || '-'}</div>
            )}
          </div>
          <div className="mt-1 flex gap-2 text-xs text-gray-500">
            {doorId && <span>Door: {doorId}</span>}
            <span>Chain: {chainId}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function ComparisonSummary({
  inputFields,
  selectedChain,
  selectedDoor,
  isChainManualMatch,
  isDoorManualMatch,
}: {
  inputFields: Array<{ label: string; value: string }>
  selectedChain?: ChainSearchResult | null
  selectedDoor?: DoorSearchResult | null
  isChainManualMatch?: boolean
  isDoorManualMatch?: boolean
}) {
  const { getCompanyUrlFromId } = useUnifiedSearchCompanyUrl()
  const hasChain = !!selectedChain
  const hasDoor = !!selectedDoor

  // Build URLs for selected chain and door
  const chainUrl = selectedChain
    ? getCompanyUrlFromId(`${selectedChain.type}${selectedChain.item_id}`)
    : null
  const doorUrl = selectedDoor
    ? getCompanyUrlFromId(`${selectedDoor.type}${selectedDoor.item_id}`)
    : null

  return (
    <div>
      <div className="grid grid-cols-3 gap-4">
        {/* Input Column - shows all mapped company fields */}
        <div>
          <div className="text-sm font-semibold text-fb-dark-green mb-2 pb-1 border-b border-fb-dark-green">
            Your Input
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {inputFields.map((field, index) => (
              <ComparisonField
                key={index}
                label={field.label}
                value={field.value}
              />
            ))}
            {inputFields.length === 0 && (
              <div className="text-sm text-gray-400 italic">
                No company fields mapped
              </div>
            )}
          </div>
        </div>

        {/* Selected Chain Column */}
        <div>
          <div
            className={`text-sm font-semibold mb-2 pb-1 border-b flex items-center gap-2 ${hasChain ? 'text-green-700 border-green-300' : 'text-gray-400 border-gray-300'}`}
          >
            Selected Chain
            {isChainManualMatch && (
              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                Manual
              </span>
            )}
          </div>
          <div className="space-y-2">
            {hasChain ? (
              <>
                <ComparisonField
                  label="Name"
                  value={selectedChain.name || '-'}
                  link={chainUrl}
                />
                <ComparisonField
                  label="Domain"
                  value={selectedChain.domain || '-'}
                />
                <ComparisonField
                  label="Size"
                  value={String(selectedChain.count || '-')}
                />
              </>
            ) : (
              <div className="text-sm text-gray-400 italic">
                No chain selected
              </div>
            )}
          </div>
        </div>

        {/* Selected Door Column */}
        <div>
          <div
            className={`text-sm font-semibold mb-2 pb-1 border-b flex items-center gap-2 ${hasDoor ? 'text-green-700 border-green-300' : 'text-gray-400 border-gray-300'}`}
          >
            Selected Door
            {isDoorManualMatch && (
              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                Manual
              </span>
            )}
          </div>
          <div className="space-y-2">
            {hasDoor ? (
              <>
                <ComparisonField
                  label="Name"
                  value={selectedDoor.name || '-'}
                  link={doorUrl}
                />
                <ComparisonField
                  label="Domain"
                  value={selectedDoor.domain || '-'}
                />
                <ComparisonField
                  label="Address"
                  value={selectedDoor.address || '-'}
                />
              </>
            ) : (
              <div className="text-sm text-gray-400 italic">
                No door selected
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ComparisonField({
  label,
  value,
  link,
}: {
  label: string
  value: string
  link?: string | null
}) {
  return (
    <div className="flex items-start gap-1.5">
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-500">{label}</div>
        <div className="text-gray-900">
          {link ? (
            <FbLink to={link} target="_blank">
              {value}
            </FbLink>
          ) : (
            value
          )}
        </div>
      </div>
    </div>
  )
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="text-sm px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-gray-800">
      {children}
    </kbd>
  )
}
